import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Ticket,
    Package,
    Bus,
    MapPin,
    Check,
    Plus,
    Minus,
    ArrowRight,
    ShieldCheck,
} from "lucide-react";
import SearchWidget from "@/components/SearchWidget";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import LocalityModal from "@/components/LocalityModal";
import { CARD_DESTINATIONS, FAQS, BUY_URL } from "@/data";

const NEEDS = [
    {
        title: "Comprar pasajes",
        desc: "Reservá tu viaje online",
        icon: Ticket,
        href: BUY_URL,
        external: true,
    },
    {
        title: "Encomiendas",
        desc: "Seguí tu envío en tiempo real",
        icon: Package,
        to: "/encomiendas",
    },
    {
        title: "Turismo",
        desc: "Alquiler de buses para grupos",
        icon: Bus,
        to: "/turismo",
    },
    {
        title: "Boleterías",
        desc: "Encontrá tu punto más cercano",
        icon: MapPin,
        to: "/boleterias",
    },
];

const VALUES = [
    {
        n: "01",
        title: "Confianza",
        desc: "Años recorriendo las rutas del país conectando familias, con la seriedad de una empresa que cumple.",
    },
    {
        n: "02",
        title: "Comodidad",
        desc: "Butacas cama y semi cama reclinables, apoya piernas y unidades de doble piso pensadas para tu descanso.",
    },
    {
        n: "03",
        title: "Seguridad",
        desc: "Choferes profesionales y mantenimiento constante para que llegues tranquilo a cada destino.",
    },
];

function NeedCard({ item }) {
    const Icon = item.icon;
    const inner = (
        <div className="group relative h-full overflow-hidden rounded-3xl border border-black/5 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-brand/10">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-light text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <Icon className="h-7 w-7" />
            </div>
            <h3 className="mt-6 text-lg font-extrabold uppercase tracking-tight text-ink">
                {item.title}
            </h3>
            <p className="mt-1.5 text-sm text-ink/60">{item.desc}</p>
            <ArrowRight className="mt-6 h-5 w-5 text-brand transition-transform group-hover:translate-x-1.5" />
        </div>
    );
    return item.external ? (
        <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`need-${item.title}`}
        >
            {inner}
        </a>
    ) : (
        <Link to={item.to} data-testid={`need-${item.title}`}>
            {inner}
        </Link>
    );
}

function Accordion() {
    const [open, setOpen] = useState(0);
    return (
        <div className="mx-auto max-w-3xl divide-y divide-black/10">
            {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                    <div key={i} data-testid={`faq-${i}`} className="py-1">
                        <button
                            onClick={() => setOpen(isOpen ? -1 : i)}
                            className="flex w-full items-center justify-between gap-4 py-5 text-left"
                        >
                            <span className="text-base sm:text-lg font-extrabold uppercase tracking-tight text-ink">
                                {f.q}
                            </span>
                            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand">
                                {isOpen ? (
                                    <Minus className="h-4 w-4" />
                                ) : (
                                    <Plus className="h-4 w-4" />
                                )}
                            </span>
                        </button>
                        <div
                            className={`grid transition-all duration-300 ${
                                isOpen
                                    ? "grid-rows-[1fr] opacity-100"
                                    : "grid-rows-[0fr] opacity-0"
                            }`}
                        >
                            <div className="overflow-hidden">
                                <p className="pb-6 text-sm leading-relaxed text-ink/70">
                                    {f.a}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function Home() {
    const [selected, setSelected] = useState(null);
    const closeModal = useCallback(() => setSelected(null), []);
    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-screen overflow-hidden bg-ink">
                <img
                    src="/images/hero-micro.webp"
                    alt="Micro de El Cometa Bis al atardecer sobre la costanera"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/30" />

                <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-4 sm:px-6 lg:px-8 pt-28 pb-16">
                    <Reveal>
                        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-brand">
                            <span className="h-px w-8 bg-brand" />
                            Viajá seguro · Viajá mejor
                        </div>
                        <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[0.95] text-white">
                            Tu próximo viaje{" "}
                            <br />
                            es con nosotros
                        </h1>
                        <p className="mt-6 max-w-lg text-base sm:text-lg text-white/80">
                            Pasajes de larga distancia, encomiendas y turismo en
                            bus. Elegí tu ruta y viajá con la confianza de El
                            Cometa Bis.
                        </p>
                    </Reveal>

                    <Reveal delay={0.15} className="mt-10 max-w-5xl">
                        <SearchWidget />
                    </Reveal>
                </div>
            </section>

            <Marquee
                items={[
                    "Próximamente",
                    "Nuevo recorrido",
                    "Puerto Iguazú ↔ La Plata",
                ]}
                variant="orange"
                sep="✦"
            />

            {/* NECESITÁS HOY */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                            Todo en un lugar
                        </p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">
                            ¿Qué necesitás hoy?
                        </h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {NEEDS.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.08}>
                                <NeedCard item={item} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <Marquee
                items={[
                    "Próximamente",
                    "Nuevo recorrido",
                    "Puerto Iguazú ↔ La Plata",
                ]}
                variant="orange"
                sep="✦"
                speed="marquee-slow"
            />

            {/* DESTINOS */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                            Elegí tu destino
                        </p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">
                            A dónde te llevamos
                        </h2>
                    </Reveal>

                    <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {CARD_DESTINATIONS.map((d, i) => (
                            <Reveal key={d.name} delay={(i % 3) * 0.06}>
                                <button
                                    type="button"
                                    onClick={() => setSelected(d)}
                                    data-testid={`destination-${d.name}`}
                                    className="group relative block h-72 w-full overflow-hidden rounded-3xl text-left"
                                >
                                    <img
                                        src={d.img}
                                        alt={`Viajá desde ${d.name}`}
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-6">
                                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">
                                            {d.region}
                                        </p>
                                        <h3 className="mt-1 text-2xl font-extrabold text-white">
                                            {d.name}
                                        </h3>
                                        <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-white transition-all duration-300 group-hover:gap-2.5">
                                            Ver servicios
                                            <ArrowRight className="h-4 w-4" />
                                        </span>
                                    </div>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <LocalityModal
                locality={selected?.name}
                image={selected?.img}
                region={selected?.region}
                onClose={closeModal}
            />

            {/* KILÓMETROS DE CONFIANZA */}
            <section className="bg-brand py-20 lg:py-28 text-white">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">
                            Nuestra manera de viajar
                        </p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">
                            Kilómetros de confianza
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-10 md:grid-cols-3">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.n} delay={i * 0.1}>
                                <div className="border-t border-white/30 pt-6">
                                    <span className="font-display text-5xl font-extrabold text-white">
                                        {v.n}
                                    </span>
                                    <h3 className="mt-4 text-xl font-extrabold uppercase tracking-tight">
                                        {v.title}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-white/85">
                                        {v.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICIOS A BORDO */}
            <section className="py-20 lg:py-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="grid items-center gap-12 lg:grid-cols-2">
                        <Reveal>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                                    Servicios a bordo
                                </p>
                                <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">
                                    Elegí cómo viajar
                                </h2>
                                <p className="mt-4 max-w-md text-base text-ink/70">
                                    Unidades modernas de doble piso con dos
                                    niveles de confort para que descanses durante
                                    todo el recorrido.
                                </p>

                                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                                    <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm">
                                        <h3 className="text-lg font-extrabold uppercase tracking-tight text-ink">
                                            Cama
                                        </h3>
                                        <ul className="mt-3 space-y-2">
                                            {[
                                                "Butacas individuales y compartidas",
                                                "Reclinables en 160°",
                                                "Apoya piernas",
                                            ].map((t) => (
                                                <li
                                                    key={t}
                                                    className="flex items-start gap-2 text-sm text-ink/70"
                                                >
                                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                                                    {t}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm">
                                        <h3 className="text-lg font-extrabold uppercase tracking-tight text-ink">
                                            Semi Cama
                                        </h3>
                                        <ul className="mt-3 space-y-2">
                                            {[
                                                "Butacas reclinables en 130°",
                                                "Apoya piernas",
                                            ].map((t) => (
                                                <li
                                                    key={t}
                                                    className="flex items-start gap-2 text-sm text-ink/70"
                                                >
                                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                                                    {t}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                                <div className="mt-4 flex items-center gap-4 rounded-3xl bg-brand p-6">
                                    <img
                                        src="/images/starlink.webp"
                                        alt="Starlink"
                                        className="h-12 w-12 object-contain"
                                    />
                                    <div>
                                        <h3 className="text-base font-extrabold uppercase tracking-tight text-white">
                                            WiFi Starlink a bordo
                                        </h3>
                                        <p className="mt-1 text-sm text-white/90">
                                            Todos nuestros micros cuentan con
                                            internet satelital Starlink para que
                                            viajes conectado durante todo el
                                            recorrido.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.12}>
                            <div className="relative overflow-hidden rounded-[32px]">
                                <img
                                    src="/images/micro-blanco.webp"
                                    alt="Micro de doble piso de El Cometa Bis"
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="pb-20 lg:pb-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal className="text-center">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
                            Preguntas frecuentes
                        </p>
                        <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-ink">
                            Antes de viajar
                        </h2>
                    </Reveal>
                    <div className="mt-12">
                        <Accordion />
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="pb-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[36px] bg-brand px-8 py-16 text-center sm:px-16">
                            <ShieldCheck className="mx-auto h-12 w-12 text-white" />
                            <h2 className="mt-6 font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
                                Tu próximo viaje es con nosotros
                            </h2>
                            <p className="mx-auto mt-4 max-w-xl text-base text-white/85">
                                Buscá origen, destino y fecha. Completá tu compra
                                de forma 100% segura en la tiquetera online.
                            </p>
                            <a
                                href={BUY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="home-final-cta"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform hover:-translate-y-0.5"
                            >
                                <Ticket className="h-4 w-4" />
                                Comprar pasajes
                            </a>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
